import { ToggleButton, ToggleButtonGroup } from "@mui/material"
import { styled } from "@mui/material/styles"

export const StyledToggleButtonGroup = styled(ToggleButtonGroup)(() => ({
  "&.MuiToggleButtonGroup-root": {
    borderRadius: "50px",
    display: "flex",
  },
  "& .MuiToggleButtonGroup-grouped": {
    borderRadius: "50px",
    border: "none",
    margin: "0",
  },
}));

interface StyledToggleButtonProps {
  variant?: 'default' | 'compact';
}

/**
 * Styled toggle button with two size variants:
 * - 'default': Larger buttons (current implementation)
 * - 'compact': Smaller buttons for more constrained spaces
 * 
 * Usage:
 * <StyledToggleButton variant="default" value="option1">Default</StyledToggleButton>
 * <StyledToggleButton variant="compact" value="option2">Compact</StyledToggleButton>
 */

const StyledToggleButton = styled(ToggleButton)<StyledToggleButtonProps>(
  ({ theme, variant = "default" }) => {
    const isCompact = variant === "compact"

    return {
      textTransform: "none",
      borderRadius: "50px",
      fontFamily: "Maven Pro, Inter, sans-serif",
      fontWeight: 400,
      fontSize: 16,
      width: "auto",
      minWidth: isCompact ? "100px" : "130px",
      padding: "0 18px",
      [theme.breakpoints.up("lg")]: {
        minWidth: isCompact ? "100px" : "140px",
        fontSize: 16,
      },
      [theme.breakpoints.only("md")]: {
        minWidth: isCompact ? "100px" : "130px",
        fontSize: 16,
      },
      [theme.breakpoints.only("sm")]: {
        minWidth: isCompact ? "60px" : "80px",
        height: "35px",
        fontSize: isCompact ? 11 : 12,
      },
      [theme.breakpoints.down("sm")]: {
        minWidth: isCompact ? "65px" : "85px",
        height: "35px",
        fontSize: isCompact ? 11 : 12,
      },
      height: "40px",
      color: "#000",
      whiteSpace: "nowrap",
      backgroundColor: "var(--toggle-bg, transparent)",
      "&:focus": {
        outline: "none",
      },
      "&.Mui-selected": {
        color: "#fff",
        fontWeight: 500,
        fontSize: 16,
        background: "linear-gradient(90deg, #929BED, #F99A7B)",
        border: "4px solid white",
        outline: "none",
        boxShadow: "0px 0px 20px 0px #00000040",
        width: "auto",
        minWidth: isCompact ? "140px" : "170px",
        padding: "0 22px",
        height : "41px",
        [theme.breakpoints.up("lg")]: {
          minWidth: isCompact ? "140px" : "180px",
          fontSize: 16,
        },
        [theme.breakpoints.only("md")]: {
          minWidth: isCompact ? "140px" : "170px",
          fontSize: 16,
        },
        [theme.breakpoints.only("sm")]: {
          minWidth: isCompact ? "80px" : "100px",
          fontSize: 13,
          border: "3px solid white",
        },
        [theme.breakpoints.down("sm")]: {
          minWidth: isCompact ? "85px" : "89px",
          fontSize: isCompact ? 12 : 13,
          border: "3px solid white",
        },
        animation: "selectGrow 0.3s ease-in",
        "&:hover": {
          backgroundColor: "#fff",
        },
      },
      "&:hover": {
        backgroundColor: "rgba(0, 0, 0, 0.1)",
      },
      border: `1px solid transparent`,
      transition: "width 0.3s",
      "@keyframes selectGrow": {},
    }
  }
)

export default StyledToggleButton
